import { extractPlaceholders } from './placeholders';

export const TEMPLATE_META_VALIDATION_CODES = {
  parameter_at_the_start: 'parameter_at_the_start',
  unsupported_parameter_at_the_end: 'unsupported_parameter_at_the_end',
  adjacent_parameters: 'adjacent_parameters',
  non_sequential_parameters: 'non_sequential_parameters',
  too_many_parameters: 'too_many_parameters',
  parameters_not_allowed: 'parameters_not_allowed',
  header_text_too_long: 'header_text_too_long',
  body_text_too_long: 'body_text_too_long',
  footer_text_too_long: 'footer_text_too_long',
  text_too_long: 'text_too_long',
};

// TODO(TM-2341): Validar los errores al editar templates contra tokko-messenger-py.
// Por ahora se deja una validación previa en el front con las reglas de Meta conocidas
// para evitar llamadas al API cuando el contenido ya es inválido. Sincronizar códigos en
// AddTemplateModal.trans.json (Crowdin) cuando el backend agregue nuevos.

const ANY_PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}/g;
const POSITIONAL_PLACEHOLDER_REGEX = /^\{\{\d+\}\}$/;
const PUNCTUATION_OR_SPACE_REGEX = /[\s.,;:!?¡¿]+$/;

const addTemplateValidationError = (errors, component, code) => {
  if (!errors[component]) {
    errors[component] = {};
  }

  errors[component][code] = null;
};

const getAnyPlaceholderMatches = (text) => [
  ...text.matchAll(ANY_PLACEHOLDER_REGEX),
];

const startsWithPlaceholder = (text) => {
  const trimmedStart = text.replace(/^\s+/, '');
  return /^\{\{[^}]+\}\}/.test(trimmedStart);
};

const endsWithPlaceholder = (text) => {
  const trimmed = text.replace(PUNCTUATION_OR_SPACE_REGEX, '');
  const matches = getAnyPlaceholderMatches(trimmed);

  if (!matches.length) {
    return false;
  }

  const lastMatch = matches[matches.length - 1];
  return lastMatch.index + lastMatch[0].length === trimmed.length;
};

const hasAdjacentPlaceholders = (text) => {
  const matches = getAnyPlaceholderMatches(text);

  for (let index = 0; index < matches.length - 1; index += 1) {
    const current = matches[index];
    const next = matches[index + 1];
    const between = text.slice(current.index + current[0].length, next.index);

    if (between.trim() === '') {
      return true;
    }
  }

  return false;
};

const sequenceIsValid = (text) => {
  const matches = getAnyPlaceholderMatches(text);

  if (!matches.length) {
    return true;
  }

  const hasNonPositionalPlaceholder = matches.some(
    (match) => !POSITIONAL_PLACEHOLDER_REGEX.test(match[0])
  );

  if (hasNonPositionalPlaceholder) {
    return true;
  }

  const numbers = matches.map((match) => Number(match[1]));
  return numbers.every((number, index) => number === index + 1);
};

const validateTextComponentMetaRules = (
  errors,
  component,
  text,
  { maxLength, textTooLongCode }
) => {
  if (text.length > maxLength) {
    addTemplateValidationError(errors, component, textTooLongCode);
  }

  if (startsWithPlaceholder(text)) {
    addTemplateValidationError(
      errors,
      component,
      TEMPLATE_META_VALIDATION_CODES.parameter_at_the_start
    );
  }

  if (endsWithPlaceholder(text)) {
    addTemplateValidationError(
      errors,
      component,
      TEMPLATE_META_VALIDATION_CODES.unsupported_parameter_at_the_end
    );
  }

  if (hasAdjacentPlaceholders(text)) {
    addTemplateValidationError(
      errors,
      component,
      TEMPLATE_META_VALIDATION_CODES.adjacent_parameters
    );
  }

  if (!sequenceIsValid(text)) {
    addTemplateValidationError(
      errors,
      component,
      TEMPLATE_META_VALIDATION_CODES.non_sequential_parameters
    );
  }
};

export const validateTemplateMetaRules = (form) => {
  const errors = {};
  const header = form.header?.trim();
  const body = form.body?.trim();
  const footer = form.footer?.trim();

  if (header) {
    validateTextComponentMetaRules(errors, 'header', header, {
      maxLength: 60,
      textTooLongCode: TEMPLATE_META_VALIDATION_CODES.header_text_too_long,
    });

    if (extractPlaceholders(header).length > 1) {
      addTemplateValidationError(
        errors,
        'header',
        TEMPLATE_META_VALIDATION_CODES.too_many_parameters
      );
    }
  }

  if (body) {
    validateTextComponentMetaRules(errors, 'body', body, {
      maxLength: 1024,
      textTooLongCode: TEMPLATE_META_VALIDATION_CODES.body_text_too_long,
    });
  }

  if (footer) {
    if (footer.length > 60) {
      addTemplateValidationError(
        errors,
        'footer',
        TEMPLATE_META_VALIDATION_CODES.footer_text_too_long
      );
    }

    if (extractPlaceholders(footer).length > 0) {
      addTemplateValidationError(
        errors,
        'footer',
        TEMPLATE_META_VALIDATION_CODES.parameters_not_allowed
      );
    }
  }

  return errors;
};

export const hasTemplateValidationErrors = (errors) =>
  Boolean(errors && Object.keys(errors).length > 0);

export const isTemplateOperationValidationError = (response) =>
  response?.operation_status === 'validation_error';

export const normalizeTemplateValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return {};
  }

  return Object.entries(errors).reduce(
    (normalized, [component, componentErrors]) => {
      if (!componentErrors || typeof componentErrors !== 'object') {
        return normalized;
      }

      normalized[component] = Object.entries(componentErrors).reduce(
        (componentNormalized, [code, message]) => {
          componentNormalized[code] =
            typeof message === 'string' && message !== code ? message : null;
          return componentNormalized;
        },
        {}
      );

      return normalized;
    },
    {}
  );
};

export const applyClientContentValidation = (form, setValidationErrors) => {
  const clientValidationErrors = validateTemplateMetaRules(form);

  if (hasTemplateValidationErrors(clientValidationErrors)) {
    setValidationErrors(clientValidationErrors);
    return false;
  }

  return true;
};

export const applyApiContentValidation = (response, setValidationErrors) => {
  if (!isTemplateOperationValidationError(response)) {
    return true;
  }

  setValidationErrors(normalizeTemplateValidationErrors(response.errors));
  return false;
};
